import { useEffect, useRef } from "react";

// Raccourcis globaux de la console. Une seule écoute au niveau du document,
// posée par AdminShell : les composants n'ajoutent pas leurs propres listeners
// globaux, sinon l'ordre de priorité devient impossible à tenir.

export const isMac = () =>
  typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || "");

export const MOD_LABEL = () => (isMac() ? "⌘" : "Ctrl");

// Destinations de la séquence « g puis … ». Les chemins sont indicatifs :
// AdminShell reçoit `onNavigate` et décide de la navigation réelle.
export const GO_TARGETS = {
  u: { label: "Utilisateurs", to: "/admin/utilisateurs" },
  o: { label: "Organisations", to: "/admin/organisations" },
  a: { label: "Journal d'audit", to: "/admin/audit" },
  i: { label: "Incidents", to: "/admin/incidents" },
};

export const SHORTCUT_GROUPS = [
  {
    title: "Général",
    items: [
      { keys: ["mod", "K"], label: "Ouvrir la palette de commandes" },
      { keys: ["/"], label: "Placer le curseur dans la recherche" },
      { keys: ["?"], label: "Afficher cette aide" },
      { keys: ["Échap"], label: "Fermer le panneau, le dialogue ou la palette" },
    ],
  },
  {
    title: "Aller à (g puis…)",
    items: [
      { keys: ["g", "u"], label: "Utilisateurs" },
      { keys: ["g", "o"], label: "Organisations" },
      { keys: ["g", "a"], label: "Journal d'audit" },
      { keys: ["g", "i"], label: "Incidents" },
    ],
  },
  {
    title: "Tables",
    items: [
      { keys: ["↑", "↓"], label: "Parcourir les résultats de la palette" },
      { keys: ["Entrée"], label: "Ouvrir l'élément sélectionné" },
      { keys: ["Maj", "clic"], label: "Étendre la sélection multiple" },
    ],
  },
];

const TYPING = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export function isTypingTarget(el) {
  if (!el) return false;
  return TYPING.has(el.tagName) || el.isContentEditable === true;
}

/* Handlers attendus :
   onPalette(), onGo(key, target), onSearch(), onHelp(), onEscape() */
export function useGlobalShortcuts(handlers, enabled = true) {
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;
    let sequence = null;
    let timer = null;

    const resetSequence = () => {
      sequence = null;
      if (timer) { clearTimeout(timer); timer = null; }
    };

    const onKey = event => {
      const h = ref.current || {};
      const key = event.key;

      // Échap et ⌘K restent actifs même dans un champ de saisie.
      if (key === "Escape") { resetSequence(); h.onEscape?.(event); return; }
      if ((event.metaKey || event.ctrlKey) && key.toLowerCase() === "k") {
        event.preventDefault();
        resetSequence();
        h.onPalette?.();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      if (sequence === "g") {
        const target = GO_TARGETS[key.toLowerCase()];
        resetSequence();
        if (target) { event.preventDefault(); h.onGo?.(key.toLowerCase(), target); }
        return;
      }
      if (key === "g") {
        sequence = "g";
        timer = setTimeout(resetSequence, 1200);
        return;
      }
      if (key === "/") { event.preventDefault(); h.onSearch?.(); return; }
      if (key === "?") { event.preventDefault(); h.onHelp?.(); }
    };

    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); resetSequence(); };
  }, [enabled]);
}

// Piège le focus dans un conteneur (dialogues, tiroirs) et rend le focus au
// retour. Tab et Maj+Tab bouclent sur les éléments focalisables visibles.
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useFocusTrap(ref, active) {
  useEffect(() => {
    if (!active || !ref.current) return undefined;
    const node = ref.current;
    const previous = document.activeElement;
    const focusables = () => Array.from(node.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement);

    const first = focusables()[0];
    (node.querySelector("[data-autofocus]") || first || node).focus?.();

    const onKey = event => {
      if (event.key !== "Tab") return;
      const items = focusables();
      if (!items.length) { event.preventDefault(); return; }
      const index = items.indexOf(document.activeElement);
      const next = event.shiftKey
        ? items[(index <= 0 ? items.length : index) - 1]
        : items[(index + 1) % items.length];
      event.preventDefault();
      next.focus();
    };

    node.addEventListener("keydown", onKey);
    return () => {
      node.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [ref, active]);
}
